<template>
  <div>
    <aside
      v-if="showOffer"
      class="pwa-install-offer elevation-5"
      aria-label="Install Self Biography"
    >
      <img
        src="/icons/app-icon.png"
        alt=""
        width="44"
        height="44"
      />
      <div class="pwa-offer-copy">
        <strong>Keep your story close</strong>
        <p>Install Self Biography for easy access from your {{ mobile ? 'home screen' : 'desktop' }}.</p>
        <div class="pwa-offer-actions">
          <v-btn
            small
            color="primary"
            @click="openInstallDialog"
          >
            Install app
          </v-btn>
          <v-btn
            small
            text
            @click="dismissInstallOffer"
          >
            Not now
          </v-btn>
        </div>
      </div>
    </aside>

    <v-dialog
      v-model="pwa.dialog"
      max-width="480"
      scrollable
      aria-labelledby="pwa-install-title"
    >
      <v-card>
        <v-card-title
          id="pwa-install-title"
          class="pwa-title"
        >
          {{ pwa.installed ? 'App installation' : 'Install Self Biography' }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            aria-label="Close installation instructions"
            @click="close"
          >
            <v-icon>{{ mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pwa-install-details">
          <template v-if="pwa.installed">
            <p>{{ pwa.standalone ? 'You are using the installed app.' : 'You have already installed Self Biography. Open it from your app icon.' }}</p>
            <v-btn
              v-if="!pwa.standalone"
              text
              color="primary"
              @click="resetInstallStatus"
            >
              Need to install again?
            </v-btn>
          </template>
          <template v-else>
            <p>Add Self Biography to your home screen or desktop and open it in its own window.</p>
            <p class="text-caption">
              An internet connection is required to access and save your stories, photos and account.
            </p>
            <v-alert
              v-if="!pwa.secure"
              type="warning"
              outlined
              dense
            >
              Open Self Biography over HTTPS to install the app.
            </v-alert>
            <v-alert
              v-if="pwa.message"
              type="info"
              outlined
              dense
              role="status"
            >
              {{ pwa.message }}
            </v-alert>
            <template v-if="pwa.nativePrompt && pwa.secure">
              <v-btn
                color="primary"
                :loading="pwa.busy"
                class="mb-4"
                @click="requestInstall"
              >
                Install now
              </v-btn>
              <p>Your browser will ask you to confirm the installation.</p>
            </template>
            <template v-else>
              <p v-if="pwa.embedded">
                <strong>Open this page in Safari or Chrome first.</strong> The browser inside another app may not support installation.
              </p>
              <template v-if="pwa.platform === 'ios'">
                <h3>On iPhone or iPad</h3>
                <ol>
                  <li>Open this page in Safari and tap <strong>Share</strong> (the square with an upward arrow). You may find Share inside the page menu.</li>
                  <li>Choose <strong>Add to Home Screen</strong>. If it is missing, scroll down or check <strong>Edit Actions</strong>.</li>
                  <li>Keep <strong>Open as Web App</strong> enabled if shown, then tap <strong>Add</strong>.</li>
                  <li>Open <strong>Self Biography</strong> from your home screen.</li>
                </ol>
              </template>
              <template v-else-if="pwa.platform === 'android'">
                <h3>On Android</h3>
                <ol>
                  <li>Open this page in Chrome or another browser that supports app installation.</li>
                  <li>Open the browser menu (⋮) and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
                  <li>Confirm the installation and open Self Biography from its app icon.</li>
                </ol>
              </template>
              <template v-else-if="pwa.platform === 'mac-safari'">
                <h3>In Safari on Mac</h3>
                <p>On supported versions, choose <strong>File → Add to Dock</strong>, then click <strong>Add</strong>.</p>
              </template>
              <template v-else>
                <h3>On your computer</h3>
                <p>In Chrome or Edge, use the install icon in the address bar, or open the browser menu and look for <strong>Install Self Biography</strong> or <strong>Install this site as an app</strong>.</p>
                <p>If the option is unavailable, try Chrome or Edge in a regular browsing window. On a supported Mac, Safari offers <strong>File → Add to Dock</strong>.</p>
              </template>
            </template>
            <v-btn
              text
              small
              color="primary"
              class="mt-4"
              @click="confirmInstalled"
            >
              I already installed it
            </v-btn>
          </template>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="close"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mdiClose } from '@mdi/js'
import {
  pwaState, canOfferInstall, openInstallDialog, dismissInstallOffer, requestInstall, confirmInstalled, resetInstallStatus,
} from '@/services/pwa'

export default {
  data: () => ({
    pwa: pwaState, mdiClose, offerReady: false, offerTimer: null,
  }),
  computed: {
    mobile() { return this.$vuetify.breakpoint.smAndDown },
    eligible() {
      const { state } = this.$store

      return canOfferInstall() && state.logged && !this.pwa.dialog
        && Date.now() >= this.pwa.dismissedUntil && this.$route.meta.auth
        && !state.agreements && !state.modal_upload && !state.modal_edit && !state.diary && !state.gallery.open
    },
    showOffer() { return this.offerReady && this.eligible },
  },
  watch: {
    'pwa.dialog': function dialogChanged(value, previous) {
      if (!value && previous && !this.pwa.installed) dismissInstallOffer()
    },
    eligible: {
      immediate: true,
      handler(value) {
        clearTimeout(this.offerTimer)
        this.offerReady = false
        if (value) this.offerTimer = setTimeout(() => { this.offerReady = true }, this.mobile ? 15000 : 45000)
      },
    },
  },
  beforeDestroy() { clearTimeout(this.offerTimer) },
  methods: {
    openInstallDialog,
    dismissInstallOffer,
    requestInstall,
    confirmInstalled,
    resetInstallStatus,
    close() { this.pwa.dialog = false; dismissInstallOffer() },
  },
}
</script>

<style scoped lang="scss">
.pwa-install-offer {
  position: fixed;
  z-index: 8;
  bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  left: 20px;
  width: min(380px, calc(100vw - 40px));
  display: flex;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: var(--v-background-base, #fff);
  color: #42394d;
  border: 1px solid rgba(145, 85, 253, 0.25);
}
.pwa-install-offer img { border-radius: 8px; flex-shrink: 0; }
.pwa-offer-copy { min-width: 0; }
.pwa-offer-copy p { font-size: 13px; line-height: 1.5; margin: 4px 0 12px; }
.pwa-offer-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.pwa-title { flex-wrap: nowrap; font-size: 20px; word-break: normal; }
.pwa-install-details h3 { font-size: 16px; margin-bottom: 12px; }
.pwa-install-details li { margin-bottom: 12px; }
.pwa-install-details .v-btn { max-width: 100%; height: auto; min-height: 36px; white-space: normal; }
.pwa-install-details ::v-deep .v-btn__content { flex: 1 1 auto; }
</style>
